import { defineComponent } from 'vue';
import { Form, FormItem, Input, InputPassword, Button } from 'ant-design-vue';
import { PhoneOutlined, LockOutlined } from '@ant-design/icons-vue';
import * as api from '@src/api/auth';
import type { LoginData } from '@src/api/auth';
import { useSendCode } from '../hooks/useSendCode';
import { useStoreLoginModal } from '@src/store/modules/loginModal';

interface RegisterData extends Omit<LoginData, 'uuid'> {
    rePassword: string;
}

export default defineComponent({
    name: 'PhoneRegister',
    emits: ['setKey'],
    setup(props, context) {
        const storeLoginModal = useStoreLoginModal();

        /**
         * data
         */
        const { sendCode, disabled: sendDisabled, loading: sendLoading, text: sendText, uuid: sendUuid } = useSendCode();

        const formLoading = ref(false);
        const formState = reactive<RegisterData>({
            loginName: '',
            password: '',
            rePassword: '',
            code: '',
        });
        const formRef = ref();

        /**
         * methods
         */
        const phoneValidator = async (_rule: any, value: string) => {
            if (!value) {
                return Promise.reject('请输入手机号');
            } else if (!/^[1][3-9][0-9]{9}$/.test(value)) {
                return Promise.reject('请输入正确手机号');
            } else {
                return Promise.resolve();
            }
        };
        const rePasswordValidator = async (_rule: any, value: string) => {
            if (!value) {
                return Promise.reject('请再次输入密码');
            } else if (value !== formState.password) {
                return Promise.reject('两次密码输入不一致');
            } else {
                return Promise.resolve();
            }
        };

        const handleSend = async () => {
            await formRef.value.validate(['loginName']);
            sendCode({ phone: formState.loginName });
        };
        const handleSubmit = async () => {
            await formRef.value.validate();
            formLoading.value = true;
            const res = await api
                .phoneRegister({
                    uuid: sendUuid.value,
                    ...formState,
                })
                .finally(() => {
                    formLoading.value = false;
                });
            await storeLoginModal.handleLoginSuccess((res as any).token);
        };

        return () => (
            <div class='phone-register'>
                <div class='phone-register-header g-flex g-items-end g-justify-between'>
                    <h3>账号注册</h3>
                    <span
                        class='g-cursor-pointer g-pr-[16px]'
                        onClick={() => {
                            context.emit('setKey', '账号密码登录');
                        }}
                    >
                        返回登录
                    </span>
                </div>
                <Form model={formState} ref={formRef}>
                    <FormItem name='loginName' rules={[{ required: true, validator: phoneValidator, trigger: 'blur' }]}>
                        <Input
                            v-model:value={formState.loginName}
                            placeholder='请输入手机号'
                            autocomplete='off'
                            v-slots={{ prefix: () => <PhoneOutlined /> }}
                        />
                    </FormItem>
                    <FormItem name='password' rules={[{ required: true, message: '请输入密码', trigger: 'blur' }]}>
                        <InputPassword
                            v-model:value={formState.password}
                            placeholder='请输入密码'
                            autocomplete='off'
                            v-slots={{ prefix: () => <LockOutlined /> }}
                        />
                    </FormItem>
                    <FormItem name='rePassword' rules={[{ required: true, validator: rePasswordValidator, trigger: 'blur' }]}>
                        <InputPassword
                            v-model:value={formState.rePassword}
                            placeholder='请再次输入密码'
                            autocomplete='off'
                            v-slots={{ prefix: () => <LockOutlined /> }}
                        />
                    </FormItem>
                    <div class='phone-code g-flex'>
                        <FormItem class='g-flex-1' name='code' rules={[{ required: true, message: '请输入验证码', trigger: 'blur' }]}>
                            <Input
                                v-model:value={formState.code}
                                onPressEnter={handleSubmit}
                                placeholder='请输入验证码'
                                autocomplete='off'
                            />
                        </FormItem>
                        <Button
                            type='primary'
                            class='g-ml-[14px] g-h-[44px] g-w-[105px] g-flex-shrink-0 g-rounded-[10px] g-font-size-[14px]'
                            loading={sendLoading.value}
                            disabled={sendDisabled.value}
                            onClick={handleSend}
                        >
                            {sendText.value}
                        </Button>
                    </div>
                    <Button
                        type='primary'
                        class='g-mt-[12px] g-h-[44px] g-w-full g-rounded-[22px] g-p-[0] g-font-size-[15px]'
                        onClick={handleSubmit}
                        loading={formLoading.value}
                    >
                        登录
                    </Button>
                </Form>
            </div>
        );
    },
});
